/**
 * Position Table Component
 *
 * Displays portfolio positions in a table layout with:
 * - Edit/Link buttons (column 0)
 * - Asset symbol + name (column 1)
 * - Allocation % (column 2)
 * - Value (column 3)
 * - Thesis status button (column 4)
 *
 * NOW THEME-AWARE and i18n SUPPORTED
 */

import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePortfolioTotal } from '../../services/api/queries/usePortfolio';
import { useUserSettings } from '../../services/api/queries/useUserSettings';
import {
  translateSector,
  translateIndustry,
  getLocalizedInstrumentName,
} from '../../utils/translationHelpers';
import { buildCurrencyRateMap, rateForCurrency } from '../../utils/currencyRates';
import { usePortfolioNavigate } from '../../navigation/PortfolioQualifierContext';
import { RowChips } from '../book/RowChips';
import type { Position } from '../../services/portfolioApi';

interface PositionTableProps {
  positions: Position[];
}

// Column widths (percentage based)
const COLUMN_WIDTHS = {
  asset: 42, // 42%
  allocation: 14, // 14%
  value: 36, // 36% - widened to hold the value + P/L stack
  warning: 8, // 8% - Warning icon for missing thesis
};

// Format a number with thousands separators and 2 decimals.
function formatAmount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Format a P/L value with an explicit sign and thousands separators. The API
// serializes Decimal fields as JSON strings, so callers must coerce with
// Number() before passing here.
function formatPL(value: number): string {
  return `${value >= 0 ? '+' : ''}${formatAmount(value)}`;
}

export function PositionTable({ positions }: PositionTableProps) {
  const navigateToPortfolio = usePortfolioNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [visibleWarningId, setVisibleWarningId] = useState<number | null>(null);

  // Auto-hide warning callout after 5 seconds
  useEffect(() => {
    if (visibleWarningId !== null) {
      const timer = setTimeout(() => {
        setVisibleWarningId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [visibleWarningId]);

  // Fetch portfolio total for currency conversion
  const { data: portfolioTotal } = usePortfolioTotal();
  const { data: userSettings } = useUserSettings();

  // Currency conversion map implied by the portfolio total's breakdown
  // (shared with the Book screen's KPI sums).
  const currencyRates = useMemo(
    () => buildCurrencyRateMap(portfolioTotal?.currency_breakdown),
    [portfolioTotal]
  );

  // Get base currency
  const baseCurrency = userSettings?.base_currency || 'USD';

  // Calculate total portfolio value in base currency
  const totalValueInBaseCurrency = useMemo(() => {
    if (!portfolioTotal?.total_value_base_currency) return 0;
    return portfolioTotal.total_value_base_currency;
  }, [portfolioTotal]);

  // Calculate allocation % for a position
  const calculateAllocation = (position: Position): string => {
    if (totalValueInBaseCurrency === 0) return '0.0%';

    const positionValue = parseFloat(position.value);
    const currency = position.currency;

    // Convert position value to base currency
    const rate = rateForCurrency(currencyRates, currency);
    const positionValueInBase = positionValue * rate;

    const allocation = (positionValueInBase / totalValueInBaseCurrency) * 100;
    return `${allocation.toFixed(1)}%`;
  };

  // Check if position has thesis
  const hasThesis = (position: Position): boolean => {
    return position.has_comments;
  };

  const handleThesisPress = (position: Position) => {
    navigateToPortfolio('PositionDetail', {
      positionId: position.id,
    });
  };

  // Display override logic: Use instrument data when linked, otherwise use position data
  const getDisplaySector = (position: Position): string | null => {
    // Prioritize position's sector (user override) over instrument's sector
    return position.sector || position.instrument_sector;
  };

  const getDisplayIndustry = (position: Position): string | null => {
    // Prioritize position's industry (user override) over instrument's industry
    return position.industry || position.instrument_industry;
  };

  const getDisplayAssetCategory = (position: Position): string => {
    if (position.instrument_id && position.instrument_asset_type) {
      return position.instrument_asset_type;
    }
    return position.asset_category;
  };

  if (positions.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>
          {t('portfolio.noPositionsFound', { defaultValue: 'No positions found' })}
        </Text>
        <Text style={styles.emptySubtext}>
          {t('portfolio.uploadCsvToStart', { defaultValue: 'Upload a CSV file to get started' })}
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.table}>
        {/* Header Row */}
        <View style={[styles.headerRow, { borderBottomColor: theme.colors.border }]}>
          <View
            style={[styles.headerCell, styles.headerCellLeft, { width: `${COLUMN_WIDTHS.asset}%` }]}
          >
            <Text style={[styles.headerText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.asset', { defaultValue: 'Asset' })}
            </Text>
          </View>
          <View
            style={[
              styles.headerCell,
              styles.headerCellCenter,
              { width: `${COLUMN_WIDTHS.allocation}%` },
            ]}
          >
            <Text style={[styles.headerText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.alloc', { defaultValue: 'Alloc' })}
            </Text>
          </View>
          <View
            style={[
              styles.headerCell,
              styles.headerCellRight,
              { width: `${COLUMN_WIDTHS.value}%` },
            ]}
          >
            <Text style={[styles.headerText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.value', { defaultValue: 'Value' })}
            </Text>
          </View>
          <View
            style={[
              styles.headerCell,
              styles.headerCellCenter,
              { width: `${COLUMN_WIDTHS.warning}%` },
            ]}
          >
            {/* Empty header for warning column */}
          </View>
        </View>

        {/* Data Rows */}
        {positions.map((position, index) => {
          const displaySector = getDisplaySector(position);
          const displayIndustry = getDisplayIndustry(position);

          // API serializes Decimal fields (unrealized_pl, realized_pl, close_price)
          // as JSON strings — coerce with Number() before formatting.
          const unrealizedPL = Number(position.unrealized_pl ?? 0);
          const realizedPL = Number(position.realized_pl ?? 0);
          const unrealizedPct = Number(position.unrealized_pl_percent ?? 0);
          // Without a current price the unrealized figure is meaningless — render a
          // neutral dash instead of a misleading green "+0.00".
          const hasPrice = position.close_price != null;

          const unrealizedColor = !hasPrice
            ? theme.colors.textTertiary
            : unrealizedPL > 0
              ? theme.colors.success
              : unrealizedPL < 0
                ? theme.colors.error
                : theme.colors.textTertiary;
          const unrealizedDisplay = !hasPrice
            ? '—'
            : `${unrealizedPL > 0 ? '▲ ' : unrealizedPL < 0 ? '▼ ' : ''}${formatPL(unrealizedPL)} (${unrealizedPct >= 0 ? '+' : ''}${unrealizedPct.toFixed(2)}%)`;

          const realizedColor =
            realizedPL > 0
              ? theme.colors.success
              : realizedPL < 0
                ? theme.colors.error
                : theme.colors.textTertiary;
          const realizedDisplay = formatPL(realizedPL);

          return (
            <View
              key={position.id}
              style={[
                styles.row,
                { borderBottomColor: theme.colors.border },
                index % 2 === 0 && styles.rowEven,
              ]}
            >
              {/* Container for the main content (spans asset, alloc, value columns) */}
              <View style={styles.rowContent}>
                {/* Symbol - on first line */}
                <View style={styles.symbolRow}>
                  <TouchableOpacity
                    style={styles.symbolContainer}
                    onPress={() => handleThesisPress(position)}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.symbolText, { color: theme.colors.text }]}>
                      {position.symbol}
                    </Text>
                  </TouchableOpacity>

                  {/* Thesis state chips (Task 9): ● covered / ⚠ stop-breach;
                      the ◔ draft chip arrives with T14. */}
                  <RowChips
                    positionId={position.id}
                    symbol={position.symbol}
                    lastPrice={position.close_price}
                    onPress={() => handleThesisPress(position)}
                  />

                  {/* Allocation and Value on same line as symbol */}
                  <TouchableOpacity
                    style={[
                      styles.cell,
                      styles.cellRight,
                      { width: `${COLUMN_WIDTHS.allocation}%`, flex: 1 },
                    ]}
                    onPress={() => handleThesisPress(position)}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.allocationText, { color: theme.colors.text }]}>
                      {calculateAllocation(position)}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.cell,
                      styles.cellRight,
                      { width: `${COLUMN_WIDTHS.value}%`, flex: 1 },
                    ]}
                    onPress={() => handleThesisPress(position)}
                    activeOpacity={0.7}
                  >
                    {/* Value + inline currency */}
                    <Text style={[styles.valueAmount, { color: theme.colors.text }]}>
                      {formatAmount(Number(position.value))}
                      <Text style={[styles.valueCurrency, { color: theme.colors.textSecondary }]}>
                        {' '}
                        {position.currency}
                      </Text>
                    </Text>
                    {/* Unrealized P/L + % (primary metric) */}
                    <Text style={[styles.unrealizedText, { color: unrealizedColor }]}>
                      {unrealizedDisplay}
                    </Text>
                    {/* Realized P/L (secondary, labelled) */}
                    <Text style={[styles.realizedText, { color: realizedColor }]}>
                      {t('portfolio.realizedShort', { defaultValue: 'Realized' })} {realizedDisplay}
                    </Text>
                  </TouchableOpacity>

                  {/* Warning column */}
                  <View
                    style={[styles.cell, styles.cellCenter, { width: `${COLUMN_WIDTHS.warning}%` }]}
                  >
                    {!hasThesis(position) && (
                      <TouchableOpacity
                        onPress={() =>
                          setVisibleWarningId(visibleWarningId === position.id ? null : position.id)
                        }
                        activeOpacity={0.7}
                      >
                        <Ionicons name="warning-outline" size={20} color={theme.colors.error} />
                      </TouchableOpacity>
                    )}
                    {visibleWarningId === position.id && (
                      <View
                        style={[
                          styles.warningCallout,
                          {
                            backgroundColor: theme.colors.surface,
                            borderColor: theme.colors.border,
                          },
                        ]}
                      >
                        <Text style={[styles.warningCalloutText, { color: theme.colors.text }]}>
                          {t('portfolio.pleaseInputInvestmentThesis', {
                            defaultValue: 'Please input investment thesis',
                          })}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>

                {/* Instrument Name - spans across data columns */}
                {position.instrument_name && (
                  <TouchableOpacity
                    style={styles.instrumentRow}
                    onPress={() => handleThesisPress(position)}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[styles.instrumentNameText, { color: theme.colors.textSecondary }]}
                    >
                      {getLocalizedInstrumentName(
                        position.instrument_name,
                        position.instrument_name_sch,
                        position.instrument_name_tch,
                        language
                      )}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Sector/Industry - spans across data columns */}
                {(displaySector || displayIndustry) && (
                  <View style={styles.sectorRow} testID={`position-sector-row-${position.id}`}>
                    <Text style={[styles.nameText, { color: theme.colors.textTertiary }]}>
                      {displaySector && displayIndustry
                        ? `${translateSector(displaySector)} / ${translateIndustry(displayIndustry)}`
                        : displaySector
                          ? translateSector(displaySector)
                          : translateIndustry(displayIndustry)}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    contentContainer: {
      paddingVertical: 8,
      paddingHorizontal: 16,
    },
    table: {
      width: '100%',
    },
    headerRow: {
      flexDirection: 'row',
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
      paddingVertical: theme.spacing.sm,
    },
    headerCell: {
      paddingHorizontal: theme.spacing.xs,
    },
    headerCellCenter: {
      alignItems: 'center',
    },
    headerCellLeft: {
      alignItems: 'flex-start',
    },
    headerCellRight: {
      alignItems: 'flex-end',
    },
    headerText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    row: {
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
      paddingVertical: theme.spacing.sm,
    },
    rowContent: {
      flexDirection: 'column',
    },
    symbolRow: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: 36,
    },
    symbolContainer: {
      flex: 1,
      paddingLeft: theme.spacing.xs,
    },
    rowEven: {
      backgroundColor: theme.colors.backgroundElevated,
    },
    cell: {
      paddingHorizontal: theme.spacing.xs,
      justifyContent: 'center',
    },
    cellCenter: {
      alignItems: 'center',
    },
    cellRight: {
      alignItems: 'flex-end',
    },
    instrumentRow: {
      flexDirection: 'row',
      paddingVertical: 2,
    },
    instrumentNameText: {
      fontSize: theme.typography.fontSizeSm,
      flex: 1,
      paddingLeft: theme.spacing.xs,
    },
    sectorRow: {
      flexDirection: 'row',
      // Indent past the leading columns, matching the trailing warning
      // column's width (the retired actions column used to own this slot;
      // referencing the removed key made this NaN).
      marginLeft: `${COLUMN_WIDTHS.warning}%`,
      paddingVertical: 2,
    },
    symbolText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.text,
    },
    nameText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textSecondary,
      marginTop: 2,
    },
    allocationText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.colors.text,
    },
    valueAmount: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.colors.text,
    },
    valueCurrency: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textSecondary,
    },
    unrealizedText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: 2,
    },
    realizedText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 1,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.text,
      marginBottom: 8,
    },
    emptySubtext: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textTertiary,
    },
    warningCallout: {
      position: 'absolute',
      bottom: 30,
      right: 8,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      minWidth: 150,
      zIndex: 10,
    },
    warningCalloutText: {
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
    },
  });
}
